import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { TrendingUp, Zap } from "lucide-react";
import { api } from "@/lib/api";

export default function SalespersonUsage() {
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/history/salesperson/usage")
      .then((r) => setUsage(r.data))
      .catch((err) => {
        toast.error(err?.response?.data?.detail || "Failed to load usage data");
      })
      .finally(() => setLoading(false));
  }, []);

  const totalCreditsSpent = usage.reduce((sum, s) => sum + s.total_credits_spent, 0);
  const totalRenders = usage.reduce((sum, s) => sum + s.total_renders, 0);

  return (
    <div className="space-y-8" data-testid="salesperson-usage-page">
      <div>
        <span className="overline">Analytics</span>
        <h1 className="mt-3 text-4xl md:text-5xl font-display font-semibold tracking-tight">
          Sales Team Credits
        </h1>
        <p className="mt-2 text-[#5C5F62]">Track how much credit each salesperson has spent.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="neivai-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#8A8D91]">Total Credits Spent</p>
              <p className="mt-2 text-4xl font-display font-semibold">{totalCreditsSpent}</p>
            </div>
            <div className="h-12 w-12 rounded-full bg-[#F3F1EC] flex items-center justify-center">
              <Zap className="h-6 w-6 text-[#C86B53]" />
            </div>
          </div>
        </div>

        <div className="neivai-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#8A8D91]">Total Renders</p>
              <p className="mt-2 text-4xl font-display font-semibold">{totalRenders}</p>
            </div>
            <div className="h-12 w-12 rounded-full bg-[#F3F1EC] flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-[#C86B53]" />
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="neivai-card p-12 text-center">
          <p className="text-[#8A8D91]">Loading...</p>
        </div>
      ) : usage.length === 0 ? (
        <div className="neivai-card p-12 text-center">
          <p className="text-[#8A8D91]">No salesperson activity yet.</p>
        </div>
      ) : (
        <div className="neivai-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5E5E5] text-left text-[#8A8D91]">
                <th className="p-4 font-medium">Salesperson</th>
                <th className="p-4 font-medium">Credits Spent</th>
                <th className="p-4 font-medium">Total Renders</th>
                <th className="p-4 font-medium">Completed</th>
                <th className="p-4 font-medium">Failed</th>
                <th className="p-4 font-medium">Last Render</th>
              </tr>
            </thead>
            <tbody>
              {usage.map((s) => (
                <tr key={s._id} className="border-b border-[#E5E5E5] last:border-0">
                  <td className="p-4 font-medium">{s.salesperson_name || "Unknown"}</td>
                  <td className="p-4">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#F3F1EC] text-[#C86B53] text-xs font-semibold">
                      <Zap className="h-3 w-3" /> {s.total_credits_spent}
                    </span>
                  </td>
                  <td className="p-4 text-[#5C5F62]">{s.total_renders}</td>
                  <td className="p-4">
                    <span className="px-2 py-0.5 rounded-full text-xs bg-[#E7F3EA] text-[#2E7D46]">
                      {s.completed_renders}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="px-2 py-0.5 rounded-full text-xs bg-[#FEF3F2] text-[#B74F4F]">
                      {s.failed_renders}
                    </span>
                  </td>
                  <td className="p-4 text-[#8A8D91]">
                    {new Date(s.last_render_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
